/**
 * lib/consent/personalization-consent.ts
 *
 * Consent gate for **CMS personalization tracking** (Ninetailed). Every analytics
 * signal the personalization SDK emits — page views, profile `identify`, native
 * component impressions/clicks, and any custom metric (e.g. the banner `ctr`
 * event) — must only be sent AFTER the visitor grants consent, exactly like GA4.
 *
 * Consent source: OneTrust. At runtime OneTrust maintains `OnetrustActiveGroups`
 * (a comma-separated string of the categories the visitor has accepted) and fires
 * a `OneTrustGroupsUpdated` event on `window` whenever consent loads or changes.
 * This module is framework-agnostic (pure read); the reactive React binding lives
 * in `use-personalization-consent.ts`.
 *
 * Category: we gate on **C0002 (Performance / Analytics)** — the same OneTrust
 * category GA4's `analytics_storage` maps to in `OneTrustScripts.tsx`, so
 * personalization measurement follows the exact same consent as the rest of our
 * analytics. If legal/compliance decides personalization needs the Targeting
 * category (C0004) instead, change the constant below (or require multiple groups).
 */

/** OneTrust category required for CMS personalization tracking (analytics; == GA4). */
export const PERSONALIZATION_CONSENT_GROUP = '2';

/**
 * True only when the visitor has granted the personalization-tracking category.
 * Safe on the server and before OneTrust loads: returns `false` (deny-by-default),
 * mirroring the `analytics_storage: 'denied'` default in `OneTrustScripts.tsx`.
 */
export function hasPersonalizationConsent(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }

  const activeGroups = window.OnetrustActiveGroups;
  if (typeof activeGroups !== 'string' || activeGroups.length === 0) {
    return false;
  }

  return activeGroups
    .split(',')
    .map((group) => group.trim())
    .includes(PERSONALIZATION_CONSENT_GROUP);
}
